import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import { UiSettingForm } from '@components/ui-forms';
import {
    ComponentWithForm,
    IMessageTemplate,
    ISettings,
    MessageTemplate,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    MessageTemplateService,
    ModalService,
    SettingService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    combineLatest,
    forkJoin,
    of as observableOf,
} from 'rxjs';
import {
    finalize,
    switchMap,
    tap,
} from 'rxjs/operators';

const EMPTY_SETTING_FORM = {
    formGroup: {},
} as UiSettingForm;

@Component({
    templateUrl: './host-settings.page.html',
    styleUrls: ['../../shared/settings.common.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MessageTemplateService],
    encapsulation: ViewEncapsulation.None,
})
export class HostSettingsPageComponent implements OnInit, ComponentWithForm {
    @ViewChild('general', { static: false })
    public set general(ref: UiSettingForm) {
        if (!this._forms['general'] && !!ref) {
            this._forms['general'] = ref;
        }
    }
    public get general() {
        return this._forms['general'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('deployment', { static: false })
    public set deployment(ref: UiSettingForm) {
        if (!this._forms['deployment'] && !!ref) {
            this._forms['deployment'] = ref;
        }
    }
    public get deployment() {
        return this._forms['deployment'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('mail', { static: false })
    public set mail(ref: UiSettingForm) {
        if (!this._forms['mail'] && !!ref) {
            this._forms['mail'] = ref;
        }
    }
    public get mail() {
        return this._forms['mail'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('security', { static: false })
    public set security(ref: UiSettingForm) {
        if (!this._forms['security'] && !!ref) {
            this._forms['security'] = ref;
        }
    }
    public get security() {
        return this._forms['security'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('scalability', { static: false })
    public set scalability(ref: UiSettingForm) {
        if (!this._forms['scalability'] && !!ref) {
            this._forms['scalability'] = ref;
        }
    }
    public get scalability() {
        return this._forms['scalability'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('application', { static: false })
    public set application(ref: UiSettingForm) {
        if (!this._forms['application'] && !!ref) {
            this._forms['application'] = ref;
        }
    }
    public get application() {
        return this._forms['application'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('customization', { static: false })
    public set customization(ref: UiSettingForm) {
        if (!this._forms['customization'] && !!ref) {
            this._forms['customization'] = ref;
        }
    }
    public get customization() {
        return this._forms['customization'] || EMPTY_SETTING_FORM;
    }

    public loading$ = new BehaviorSubject(true);
    public settings: ISettings | null = null;
    public template = {} as IMessageTemplate;
    public activeTab = 'General';

    public get invalid(): boolean {
        return this.general.formGroup.invalid ||
            this.deployment.formGroup.invalid ||
            this.mail.formGroup.invalid ||
            this.security.formGroup.invalid ||
            this.scalability.formGroup.invalid ||
            this.application.formGroup.invalid ||
            this.customization.formGroup.invalid;
    }

    public get pristine(): boolean {
        return this.general.formGroup.pristine &&
            this.deployment.formGroup.pristine &&
            this.mail.formGroup.pristine &&
            this.security.formGroup.pristine &&
            this.scalability.formGroup.pristine &&
            this.application.formGroup.pristine &&
            this.customization.formGroup.pristine;
    }

    public get hasUnsavedChanges(): boolean {
        return !this.pristine;
    }

    private _forms: { [key: string]: UiSettingForm } = {};

    constructor(
        public settingService: SettingService,
        private _messageTemplateService: MessageTemplateService,
        private _modalService: ModalService,
        private _translateService: TranslateService,
        private _snack: UiSnackBarService,
        private _appState: AppStateFacade,
    ) { }

    ngOnInit() {
        forkJoin({
            settings: this.settingService.getServerSettings(),
            template: this._messageTemplateService.getTemplate(MessageTemplate.LoginLayout),
        }).pipe(
            finalize(() => this.loading$.next(false)),
        )
            .subscribe(
                ({ settings, template }) => {
                    this.settings = settings;
                    this.template = template;
                },
            );
    }

    public onSubmit() {
        this._checkEnableFolders();
    }

    private _checkEnableFolders() {
        const enableFolders = this.general.formGroup.get('isModernFolderSystemEnabled')!;
        const enableFoldersDirty = enableFolders.dirty;

        if (!enableFoldersDirty || !this.settings!.general.isModernFolderSystemEnabled) {
            this._checkChanges();
        } else {
            this._modalService.onConfirm(
                null,
                () => this._checkChanges(),
                () => {
                    enableFolders.setValue(this.settings!.general.isModernFolderSystemEnabled = false);
                    this._checkChanges();
                },
                this._translateService.instant('settings_general_enable_modern_sure'),
                true,
            );
        }
    }

    private _checkChanges() {
        const overridable = ['general', 'deployment', 'mail', 'security'];
        const isOverridableChanged = overridable.some(setting => (this as any)[setting].formGroup.dirty);

        if (isOverridableChanged) {
            // confirm if anything other than the Application changed
            this._modalService.onConfirm(null,
                () => this._onSubmit(),
                () => { },
                this._translateService.instant('settings_save_warning_message'));
        } else {
            // save directly if only the application settings changed
            this._onSubmit();
        }
    }

    private _onSubmit() {
        if (this.pristine) { return; }

        this.loading$.next(true);

        const messageTemplateUpdate$ = this.customization.formGroup.dirty ?
            this._messageTemplateService
                .update(MessageTemplate.LoginLayout, this.template) :
            observableOf(null);

        combineLatest([
            this.settingService
                .updateSettingsBulk(this.settings!, {
                    'General': this.general.formGroup.dirty,
                    'Deployment': this.deployment.formGroup.dirty,
                    'Mail': this.mail.formGroup.dirty,
                    'Security': this.security.formGroup.dirty,
                    'Scalability': this.scalability.formGroup.dirty,
                    'Application': this.application.formGroup.dirty,
                }),
            messageTemplateUpdate$,
        ])
            .pipe(
                switchMap(() => this._appState.settings.fetch()),
                tap(() => {
                    this._snack.success(this._translateService.instant('settings_save_success_message'));

                    this.settings = cloneDeep(this.settings);
                    this.general.formGroup.reset(this.general.formGroup.value);
                    this.deployment.formGroup.reset(this.deployment.formGroup.getRawValue());
                    this.mail.formGroup.reset(this.mail.formGroup.value);
                    this.security.formGroup.reset(this.security.formGroup.value);
                    this.application.formGroup.reset(this.application.formGroup.value);
                    this.scalability.formGroup.reset(this.scalability.formGroup.getRawValue());
                    this.customization.formGroup.reset(this.customization.formGroup.value);
                }),
                switchMap(() => this.settingService.getServerSettings()),
                finalize(() => this.loading$.next(false)),
            )
            .subscribe((newSettings) => this.settings = newSettings);
    }
}
